import styles from './usersSection.module.scss';
import { FC, useMemo, useState } from 'react';
import { useGetStudyStaffPerRoleQuery } from 'src/services/api/api-client/StudyQuery';
import { StudyInfoPageSection } from '../../layoutComponents/StudyInfoPageLayout';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { useStudy } from 'src/helpers/hooks/useStudy';
import { AppTable } from 'uikit/table/AppTable';
import { ColumnDef, ColumnSort, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { IStudyStaffPerRoleDto, StudyRoleClaimValue } from 'src/services/api/api-client';
import { AppButton } from 'uikit/buttons';
import { useNavigate } from 'react-router';
import { navigateToRoute } from 'src/navigation/routes';
import { useHasStudyClaims } from 'src/helpers/auth/auth-helper';

export const UsersSection = () => {
  const { id: studyId } = useStudy() ?? {};
  const { t } = useScopedTranslation('StudyInfoPage.UsersSection');
  const usersPerRoleQuery = useGetStudyStaffPerRoleQuery(studyId!, { keepPreviousData: true });
  const { hasStudyClaim } = useHasStudyClaims();

  const [sorting, setSorting] = useState<ColumnSort[]>([{ id: 'count', desc: true }]);
  const rowDataTable = useReactTable<IStudyStaffPerRoleDto>({
    data: usersPerRoleQuery?.data || [],
    getRowId: (dto) => dto.roleName,
    columns: useColumn(),
    state: { sorting },
    enableSortingRemoval: false,
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <StudyInfoPageSection title={t('Title')} variant={'flex'} isFullHeight={true}>
      <AppTable
        table={rowDataTable}
        placeholder={{ hide: usersPerRoleQuery.isLoading || usersPerRoleQuery.isPreviousData, text: t('Placeholder') }}
        customRow={hasStudyClaim(StudyRoleClaimValue.StudyUserListView) ? <LastTableRow /> : undefined}
        customRowClassName={styles.lastRowWithButton}
      />
    </StudyInfoPageSection>
  );
};

const LastTableRow: FC = () => {
  const { id: studyId } = useStudy() ?? {};
  const { t } = useScopedTranslation('StudyInfoPage.UsersSection');
  const navigate = useNavigate();

  return (
    <AppButton
      variant={'icon-link'}
      colorSchema={'primary'}
      text={t('ShowAllUsersButton')}
      iconClassName={styles.showUsersIcon}
      onClick={() => navigate(navigateToRoute.studyUsersPage(studyId!))}
    />
  );
};

const useColumn = (): ColumnDef<IStudyStaffPerRoleDto>[] => {
  const { t } = useScopedTranslation('StudyInfoPage.UsersSection');

  return useMemo(
    () =>
      [
        {
          id: 'roleName',
          header: t('Headers.Role'),
          enableSorting: false,
          size: 200,
          cell: ({ row }) => row.original.roleName,
          meta: { cellClassName: styles.cell, headerClassName: styles.header },
        },
        {
          id: 'count',
          accessorFn: (dto) => dto.staffCount,
          enableSorting: true,
          header: t('Headers.StaffCount'),
          cell: ({ row }) => row.original.staffCount,
          size: 200,
          meta: { cellClassName: styles.cell, headerClassName: styles.header },
        },
      ] satisfies ColumnDef<IStudyStaffPerRoleDto>[],
    [t],
  );
};
